/**
 * Sawyer 06 beds-lockers-runcard (B2, B4) — pure helpers for a bed's size
 * class, its default locker and the tenant's size list (Settings → Lodging).
 *
 * Sawyer, 2026-10-23 (memo2 [04:01]): "we could just have a bed number and
 * then a ... size". The size LIST is the owner's (operations.bedSizeClasses,
 * default Small / Big). A bed keeps a size later dropped from the list, and
 * the card says so ("Big (not in your size list)") instead of blanking it
 * (Design Philosophy principle 5, never lie about data).
 */

/** Mirrors the server's MAX_BED_SIZE_CLASSES / MAX_BED_SIZE_CLASS_LENGTH / MAX_LOCKER_LENGTH. */
export const MAX_BED_SIZES = 12;
export const MAX_BED_SIZE_LENGTH = 20;
export const MAX_LOCKER_LENGTH = 40;

/** The server's own sentences (packages/core/tenant/get-tenant-operations-config.ts), so both sides say the same thing. */
export const SIZES_TOO_MANY = 'Keep it to 12 sizes or fewer.';
export const SIZES_DUPLICATE = 'Each size can appear once.';
export const SIZE_TOO_LONG = 'Keep each size to 20 characters or fewer.';
export const DEFAULT_LOCKER_TOO_LONG = 'Keep the default locker to 40 characters or fewer.';
export const NO_SIZES_HINT = 'No sizes set. Add sizes above if you sort beds by size.';
export const OFF_LIST_SUFFIX = ' (not in your size list)';

export type SizeListResult = { ok: true; sizes: string[] } | { ok: false; message: string };

/** "Small, Medium , Big" → ['Small', 'Medium', 'Big']; blanks dropped; the owner's order and case kept. */
export function parseSizeList(raw: string): SizeListResult {
  const sizes = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (sizes.length > MAX_BED_SIZES) return { ok: false, message: SIZES_TOO_MANY };
  if (sizes.some((s) => s.length > MAX_BED_SIZE_LENGTH)) return { ok: false, message: SIZE_TOO_LONG };
  if (new Set(sizes.map((s) => s.toLocaleLowerCase())).size !== sizes.length) return { ok: false, message: SIZES_DUPLICATE };
  return { ok: true, sizes };
}

/** The list as the card prints it. */
export function formatSizeList(sizes: ReadonlyArray<string>): string {
  return sizes.join(', ');
}

/** Whether `size` is on the list (ignoring case). An unknown list (older server) never calls a size off-list. */
export function isOnSizeList(size: string, sizes: ReadonlyArray<string> | undefined): boolean {
  if (sizes === undefined) return true;
  const wanted = size.toLocaleLowerCase();
  return sizes.some((s) => s.toLocaleLowerCase() === wanted);
}

/** "Big", or "Big (not in your size list)"; null for a bed with no size. */
export function sizeLabel(size: string | null | undefined, sizes: ReadonlyArray<string> | undefined): string | null {
  if (!size) return null;
  return isOnSizeList(size, sizes) ? size : `${size}${OFF_LIST_SUFFIX}`;
}

/** The Size select's options: "No size", the tenant's list, and the bed's current off-list size so it is never silently lost. */
export function sizeOptions(
  sizes: ReadonlyArray<string>,
  current: string | null | undefined,
): Array<{ value: string; label: string }> {
  const opts = [{ value: '', label: 'No size' }, ...sizes.map((s) => ({ value: s, label: s }))];
  if (current && !isOnSizeList(current, sizes)) opts.push({ value: current, label: `${current}${OFF_LIST_SUFFIX}` });
  return opts;
}

/** The Size field is offered only when the server carries a non-empty list. */
export function showSizeField(sizes: ReadonlyArray<string> | undefined): sizes is ReadonlyArray<string> {
  return sizes !== undefined && sizes.length > 0;
}

/** A default-locker field → the PATCH value: blank clears (null). */
export function defaultLockerValue(raw: string): { ok: true; value: string | null } | { ok: false; message: string } {
  const t = raw.trim();
  if (t.length === 0) return { ok: true, value: null };
  if (t.length > MAX_LOCKER_LENGTH) return { ok: false, message: DEFAULT_LOCKER_TOO_LONG };
  return { ok: true, value: t };
}
